import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { Wrench, ArrowRight, Search, Loader2 } from "lucide-react";

const formatPrice = (s) => {
  if (s.price_text) return s.price_text;
  if (s.price === 0) return "Quote";
  const prefix = s.price_from ? "From " : "";
  return `${prefix}$${Number(s.price).toFixed(2)}`;
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([])).finally(() => setLoading(false));
  }, []);

  const vehicles = useMemo(() => Array.from(new Set(services.map((s) => s.vehicle))).sort(), [services]);
  const categories = useMemo(() => Array.from(new Set(services.map((s) => s.category))), [services]);

  const filtered = useMemo(() => {
    return services.filter((s) => {
      if (vehicle && s.vehicle !== vehicle) return false;
      if (category && s.category !== category) return false;
      if (search) {
        const q = search.toLowerCase();
        if (![s.name, s.summary, s.sku].some((f) => (f || "").toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [services, search, vehicle, category]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((s) => { (map[s.category] ||= []).push(s); });
    return map;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-spark-floor" data-testid="services-page">
      <Navbar />

      <div className="bg-spark-teal-deep text-white border-b-4 border-spark-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="label-mono text-spark-yellow mb-2">What we fix · how much</p>
            <h1 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95]">Services & Pricing</h1>
            <p className="mt-4 text-slate-300 max-w-xl">Honest, itemised pricing for every job we offer. All labour is covered by our 21-day service warranty.</p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <Link to="/book" className="btn-accent" data-testid="services-book-btn">Book Now <ArrowRight className="size-4" /></Link>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="surface-card p-4 sm:p-5 mb-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2 relative">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-spark-slate" />
            <input placeholder="Search services, SKU, keywords…" className="input-style pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="services-search" />
          </div>
          <select className="input-style" value={vehicle} onChange={(e) => setVehicle(e.target.value)} data-testid="services-vehicle">
            <option value="">All vehicle types</option>
            {vehicles.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className="input-style" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="services-category">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="size-6 animate-spin inline text-spark-teal" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-16 text-spark-slate" data-testid="services-empty">No services match those filters.</p>
        ) : (
          Object.keys(grouped).map((cat) => (
            <section key={cat} className="mb-10" data-testid={`category-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-end justify-between mb-4 border-b-2 border-spark-ink pb-2">
                <h2 className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-bold">{cat}</h2>
                <span className="label-mono">{grouped[cat].length} service{grouped[cat].length === 1 ? "" : "s"}</span>
              </div>
              <ul className="grid gap-px bg-spark-line border border-spark-line rounded-md overflow-hidden">
                {grouped[cat].map((s) => (
                  <li key={s.id} className="bg-white p-5 sm:p-6 grid sm:grid-cols-12 gap-4 hover:bg-spark-teal-light/40 transition-colors">
                    <div className="sm:col-span-7">
                      <div className="flex items-center gap-3 flex-wrap mb-1.5">
                        <h3 className="font-display uppercase tracking-tight text-lg sm:text-xl font-bold">{s.name}</h3>
                        <span className="label-mono text-spark-teal">{s.vehicle}</span>
                      </div>
                      <p className="text-sm text-spark-slate leading-relaxed">{s.summary}</p>
                    </div>
                    <div className="sm:col-span-3">
                      <p className="label-mono mb-1">SKU</p>
                      <p className="font-mono text-xs text-spark-ink">{s.sku}</p>
                    </div>
                    <div className="sm:col-span-2 sm:text-right">
                      <p className="label-mono mb-1">Price</p>
                      <p className="font-display uppercase tracking-tight text-2xl font-bold text-spark-ink">{formatPrice(s)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}

        <div className="bg-spark-teal text-white rounded-md p-6 sm:p-10 mt-12 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <p className="label-mono text-spark-yellow mb-2">Not sure which service?</p>
            <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl font-bold">Drop it in — we'll diagnose first.</h2>
            <p className="text-white/85 mt-3 max-w-lg">Free visual inspection. Diagnostic fee credited if you proceed with the repair.</p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-3">
            <Link to="/book" className="btn-accent" data-testid="services-cta-book"><Wrench className="size-4" /> Book Repair</Link>
            <Link to="/contact" className="btn-outline-ink !border-white !text-white hover:!bg-white hover:!text-spark-teal" data-testid="services-cta-contact">Contact</Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Services;
